import { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  getAdminCourses,
  createAdminCourse,
  updateAdminCourse,
  deleteAdminCourse,
  createAdminCourseClass,
  updateAdminCourseClass,
  deleteAdminCourseClass,
  getAdminCoursePurchases,
  revokeCourseAccess,
  deleteCloudinaryImage,
} from '../../services/adminService';
import GrantCourseAccessModal from './courses/GrantCourseAccessModal';
import CloudinaryImageUpload from '../utilPages/CloudinaryImageUpload';
import { formatPassPriceShort } from '../../utils/currencyFormat';
import { cloudinaryPublicId } from '../../utils/cloudinaryUtils';

const COURSE_THUMBNAIL_FOLDER = 'onceUpOnAMat/courses';
const CONTENT_BLOCK_TYPES = ['text', 'youtube', 'pdf', 'image'];

const EMPTY_COURSE_FORM = {
  title: '',
  slug: '',
  description: '',
  thumbnail: '',
  language: 'russian',
  order: 0,
  isPublished: false,
  isFree: false,
  nisPrice: 0,
  rubPrice: 0,
  eurDisplayPrice: '',
  emailActive: false,
  emailSubjectRu: '',
  emailBodyRu: '',
  emailSubjectEn: '',
  emailBodyEn: '',
};

const mapCourseToForm = (course) => ({
  title: course.title || '',
  slug: course.slug || '',
  description: course.description || '',
  thumbnail: course.thumbnail || '',
  language: course.language || 'russian',
  order: course.order || 0,
  isPublished: !!course.isPublished,
  isFree: !!course.isFree,
  nisPrice: course.nisPrice || 0,
  rubPrice: course.rubPrice || 0,
  eurDisplayPrice: course.eurDisplayPrice == null ? '' : course.eurDisplayPrice,
  emailActive: !!course.emailTemplate?.active,
  emailSubjectRu: course.emailTemplate?.subjectRu || '',
  emailBodyRu: course.emailTemplate?.bodyRu || '',
  emailSubjectEn: course.emailTemplate?.subjectEn || '',
  emailBodyEn: course.emailTemplate?.bodyEn || '',
});

const EMPTY_CLASS_FORM = {
  title: '',
  slug: '',
  summary: '',
  order: 0,
  isPublished: false,
  contentBlocks: [],
};

const mapClassToForm = (cls) => ({
  title: cls.title || '',
  slug: cls.slug || '',
  summary: cls.summary || '',
  order: cls.order || 0,
  isPublished: !!cls.isPublished,
  contentBlocks: (cls.contentBlocks || []).map((b) => ({ ...b })),
});

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const Pill = ({ children, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200',
    amber: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

// Small labeled section used to break an edit form into scannable groups.
const Section = ({ title, description, children, first = false }) => (
  <div className={first ? '' : 'pt-5 border-t border-gray-100'}>
    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h4>
    {description && <p className="text-xs text-gray-400 mt-0.5 mb-3">{description}</p>}
    <div className={description ? '' : 'mt-3'}>{children}</div>
  </div>
);

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="flex items-start gap-3 w-fit">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brown focus-visible:ring-offset-2 ${
        checked ? 'bg-brown' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
    <span>
      <span className="block text-sm font-medium text-gray-700">{label}</span>
      {description && <span className="block text-xs text-gray-400">{description}</span>}
    </span>
  </div>
);

const inputClass =
  'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brown focus:border-brown';

const ContentBlocksEditor = ({ blocks, setBlocks }) => {
  const updateBlock = (idx, field, value) => {
    setBlocks((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addBlock = () => {
    setBlocks((prev) => [...prev, { type: 'text', text: '', url: '', title: '', caption: '', order: prev.length }]);
  };

  const removeBlock = (idx) => {
    setBlocks((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="space-y-2">
        {blocks.map((block, idx) => (
          <div key={idx} className="border border-gray-200 rounded-md p-2.5 space-y-1.5 bg-gray-50/50">
            <div className="flex gap-2">
              <select
                value={block.type}
                onChange={(e) => updateBlock(idx, 'type', e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
              >
                {CONTENT_BLOCK_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <input
                value={block.title || ''}
                onChange={(e) => updateBlock(idx, 'title', e.target.value)}
                placeholder="title"
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
              />
              <button
                type="button"
                onClick={() => removeBlock(idx)}
                className="text-red-600 text-xs px-1"
                aria-label="Remove block"
              >
                ×
              </button>
            </div>
            {block.type === 'text' ? (
              <textarea
                value={block.text || ''}
                onChange={(e) => updateBlock(idx, 'text', e.target.value)}
                placeholder="text"
                rows={2}
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs resize-y"
              />
            ) : (
              <input
                value={block.url || ''}
                onChange={(e) => updateBlock(idx, 'url', e.target.value)}
                placeholder={block.type === 'youtube' ? 'https://youtube.com/watch?v=... or embed url' : 'url'}
                className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
              />
            )}
            <input
              value={block.caption || ''}
              onChange={(e) => updateBlock(idx, 'caption', e.target.value)}
              placeholder="caption (optional)"
              className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
            />
          </div>
        ))}
        {blocks.length === 0 && <p className="text-xs text-gray-400">No content blocks yet.</p>}
      </div>
      <button type="button" onClick={addBlock} className="text-sm text-brown hover:underline mt-2">
        + Add block
      </button>
    </div>
  );
};

const ClassRow = ({ courseId, cls, token, setToaster, isNew = false, startExpanded = false, onSaved, onCreated, onDeleted, onDiscard }) => {
  const [expanded, setExpanded] = useState(startExpanded);
  const [form, setForm] = useState(() => (isNew ? EMPTY_CLASS_FORM : mapClassToForm(cls)));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const setContentBlocks = (updater) => {
    setForm((f) => ({ ...f, contentBlocks: typeof updater === 'function' ? updater(f.contentBlocks) : updater }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim().toLowerCase(),
      summary: form.summary.trim(),
      order: Number(form.order) || 0,
      isPublished: form.isPublished,
      contentBlocks: form.contentBlocks,
    };
    try {
      if (isNew) {
        const created = await createAdminCourseClass(token, courseId, payload);
        setToaster({ message: 'Class added', type: 'success', show: true });
        onCreated(created);
      } else {
        const updated = await updateAdminCourseClass(token, courseId, cls._id, payload);
        setToaster({ message: 'Class updated', type: 'success', show: true });
        onSaved(updated);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save class');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete class "${form.title}"?`)) return;
    setDeleting(true);
    setError('');
    try {
      await deleteAdminCourseClass(token, courseId, cls._id);
      setToaster({ message: 'Class deleted', type: 'success', show: true });
      onDeleted(cls._id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete class');
      setDeleting(false);
    }
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-white transition-shadow ${
        expanded ? 'border-brown/30 shadow-sm' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition"
      >
        <ChevronIcon open={expanded} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-800 truncate">{form.title || (isNew ? 'New class' : '')}</p>
          {form.slug && <p className="text-xs text-gray-400 truncate font-mono">/{form.slug}</p>}
        </div>
        {!form.isPublished && <Pill>Unpublished</Pill>}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-3 py-4 space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Slug *</label>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Summary</label>
            <textarea
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              rows={2}
              className={`${inputClass} resize-y`}
            />
          </div>
          <div className="flex items-end gap-4">
            <div className="w-24">
              <label className="block text-xs text-gray-500 mb-1.5">Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                className={inputClass}
              />
            </div>
            <ToggleSwitch
              label="Published"
              checked={form.isPublished}
              onChange={(v) => setForm((f) => ({ ...f, isPublished: v }))}
            />
          </div>

          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Content blocks (text, video, pdf, image)</p>
            <ContentBlocksEditor blocks={form.contentBlocks} setBlocks={setContentBlocks} />
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || deleting}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-brown text-white shadow-sm hover:opacity-90 disabled:opacity-50 transition"
            >
              {saving ? 'Saving…' : isNew ? 'Add class' : 'Save'}
            </button>
            {isNew ? (
              <button
                type="button"
                onClick={onDiscard}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
              >
                Discard
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving || deleting}
                className="px-3 py-1.5 text-xs font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CourseRow = ({ course, token, setToaster, isNew = false, startExpanded = false, onSaved, onCreated, onDeleted, onDiscard }) => {
  const [expanded, setExpanded] = useState(startExpanded);
  const [form, setForm] = useState(() => (isNew ? EMPTY_COURSE_FORM : mapCourseToForm(course)));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const pendingThumbnailUploadsRef = useRef(new Set());

  const [classes, setClasses] = useState(() => (course.classes || []).slice());
  const [classDrafts, setClassDrafts] = useState([]);
  const [nextClassDraftId, setNextClassDraftId] = useState(0);

  const [purchases, setPurchases] = useState(null); // null = not loaded yet
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [revokingPurchaseId, setRevokingPurchaseId] = useState(null);
  const [grantModalOpen, setGrantModalOpen] = useState(false);

  useEffect(() => {
    if (isNew || !expanded || purchases !== null) return;
    setLoadingPurchases(true);
    getAdminCoursePurchases(token, course._id)
      .then((data) => setPurchases(data.purchases || []))
      .catch((err) => {
        console.error('Error fetching course purchases:', err);
        setToaster({ message: 'Failed to load buyers', type: 'error', show: true });
      })
      .finally(() => setLoadingPurchases(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, isNew, course._id, token, purchases]);

  const discardPendingThumbnailUploads = () => {
    pendingThumbnailUploadsRef.current.forEach((url) => {
      deleteCloudinaryImage(token, cloudinaryPublicId(url)).catch(() => {});
    });
    pendingThumbnailUploadsRef.current.clear();
  };

  const handleThumbnailUploaded = ({ newUrl, replacedUrl }) => {
    if (replacedUrl) {
      pendingThumbnailUploadsRef.current.delete(replacedUrl);
      deleteCloudinaryImage(token, cloudinaryPublicId(replacedUrl)).catch(() => {});
    }
    if (newUrl) pendingThumbnailUploadsRef.current.add(newUrl);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim().toLowerCase(),
      description: form.description.trim(),
      thumbnail: form.thumbnail.trim(),
      language: form.language,
      order: Number(form.order) || 0,
      isPublished: form.isPublished,
      isFree: form.isFree,
      nisPrice: Number(form.nisPrice) || 0,
      rubPrice: Number(form.rubPrice) || 0,
      eurDisplayPrice: Number(form.eurDisplayPrice) || null,
      emailTemplate: {
        active: form.emailActive,
        subjectRu: form.emailSubjectRu.trim(),
        bodyRu: form.emailBodyRu.trim(),
        subjectEn: form.emailSubjectEn.trim(),
        bodyEn: form.emailBodyEn.trim(),
      },
    };
    try {
      if (isNew) {
        const created = await createAdminCourse(token, payload);
        pendingThumbnailUploadsRef.current.clear();
        setToaster({ message: 'Course created', type: 'success', show: true });
        onCreated(created);
      } else {
        const updated = await updateAdminCourse(token, course._id, payload);
        pendingThumbnailUploadsRef.current.clear();
        setToaster({ message: 'Course updated', type: 'success', show: true });
        onSaved({ ...updated, classes });
        setSaved(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    discardPendingThumbnailUploads();
    onDiscard();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete course "${form.title}"? This also deletes its classes.`)) return;
    setDeleting(true);
    setError('');
    try {
      await deleteAdminCourse(token, course._id);
      setToaster({ message: 'Course deleted', type: 'success', show: true });
      onDeleted(course._id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete course');
      setDeleting(false);
    }
  };

  const handleAddClassDraft = () => {
    setClassDrafts((prev) => [...prev, nextClassDraftId]);
    setNextClassDraftId((id) => id + 1);
  };

  const handleClassCreated = (draftId, created) => {
    setClassDrafts((prev) => prev.filter((id) => id !== draftId));
    setClasses((prev) => [...prev, created]);
  };

  const handleClassDiscard = (draftId) => {
    setClassDrafts((prev) => prev.filter((id) => id !== draftId));
  };

  const handleClassSaved = (updated) => {
    setClasses((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
  };

  const handleClassDeleted = (id) => {
    setClasses((prev) => prev.filter((c) => c._id !== id));
  };

  const handleRevokeAccess = async (purchase) => {
    const label = purchase.userId?.name || purchase.userId?.email || purchase.user_id;
    if (!window.confirm(`Remove ${label}'s access to this course?`)) return;
    setRevokingPurchaseId(purchase._id);
    try {
      await revokeCourseAccess(token, purchase.user_id, purchase._id);
      setPurchases((prev) => (prev || []).filter((p) => p._id !== purchase._id));
      setToaster({ message: 'Access removed', type: 'success', show: true });
    } catch (err) {
      setToaster({ message: err.message || 'Failed to remove access', type: 'error', show: true });
    } finally {
      setRevokingPurchaseId(null);
    }
  };

  const sortedClasses = classes.slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div
      className={`border rounded-xl overflow-hidden bg-white transition-shadow ${
        expanded ? 'border-brown/30 shadow-sm' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition"
      >
        <ChevronIcon open={expanded} />
        {form.thumbnail ? (
          <img src={form.thumbnail} alt="" className="w-10 h-10 rounded-md object-cover bg-gray-100 shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-md bg-gray-100 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800 truncate">
            {form.title || (isNew ? 'New course' : '')}
          </p>
          {!isNew && (
            <p className="text-xs text-gray-400 truncate font-mono">
              /{form.slug} · {classes.length} {classes.length === 1 ? 'class' : 'classes'}
            </p>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-1.5 shrink-0">
          {!isNew && form.isFree && <Pill tone="green">Free</Pill>}
          {!isNew && !form.isFree && (form.nisPrice > 0 || form.rubPrice > 0) && (
            <Pill tone="green">₪{form.nisPrice || 0} / ₽{form.rubPrice || 0}</Pill>
          )}
          {!form.isPublished && <Pill>Unpublished</Pill>}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 sm:px-6 py-5 text-left">
          <div className={isNew ? '' : 'grid grid-cols-1 lg:grid-cols-2 gap-8'}>
            <div className="space-y-0">
              <Section title="Details" first>
                <div className="space-y-3 max-w-sm">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Title *</label>
                    <input
                      value={form.title}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, title: e.target.value }));
                      }}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Slug *</label>
                    <input
                      value={form.slug}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, slug: e.target.value }));
                      }}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, description: e.target.value }));
                      }}
                      rows={2}
                      className={`${inputClass} resize-y`}
                    />
                  </div>
                  <CloudinaryImageUpload
                    label="Thumbnail"
                    folder={COURSE_THUMBNAIL_FOLDER}
                    value={form.thumbnail}
                    onChange={(url) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, thumbnail: url }));
                    }}
                    onUploaded={handleThumbnailUploaded}
                    token={token}
                  />
                </div>
              </Section>

              <Section title="Display">
                <div className="space-y-4 max-w-sm">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Language</label>
                    <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                      {[['russian', 'RU'], ['english', 'EN']].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            setSaved(false);
                            setForm((f) => ({ ...f, language: value }));
                          }}
                          className={`px-3 py-1 text-xs font-semibold rounded transition ${
                            form.language === value ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="max-w-[8rem]">
                    <label className="block text-xs text-gray-500 mb-1.5">Order</label>
                    <input
                      type="number"
                      value={form.order}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, order: e.target.value }));
                      }}
                      className={inputClass}
                    />
                  </div>
                  <ToggleSwitch
                    label="Published"
                    checked={form.isPublished}
                    onChange={(v) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, isPublished: v }));
                    }}
                  />
                </div>
              </Section>

              <Section title="Pricing">
                <div className="space-y-3 max-w-sm">
                  <ToggleSwitch
                    label="Free"
                    description="Open to everyone, no purchase required - price fields below are ignored while this is on."
                    checked={form.isFree}
                    onChange={(v) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, isFree: v }));
                    }}
                  />
                  <div className={`grid grid-cols-2 gap-3 ${form.isFree ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Price, ₪ (Allpay)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.nisPrice}
                      disabled={form.isFree}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, nisPrice: e.target.value }));
                      }}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Price, ₽ (YooKassa)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.rubPrice}
                      disabled={form.isFree}
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, rubPrice: e.target.value }));
                      }}
                      className={inputClass}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1.5">Display price, € (approximate, never charged)</label>
                    <input
                      type="number"
                      min="0"
                      value={form.eurDisplayPrice}
                      disabled={form.isFree}
                      placeholder="Leave empty to hide"
                      onChange={(e) => {
                        setSaved(false);
                        setForm((f) => ({ ...f, eurDisplayPrice: e.target.value }));
                      }}
                      className={inputClass}
                    />
                  </div>
                  </div>
                </div>
              </Section>

              <Section
                title="Confirmation email"
                description={`Only sent when enabled with a subject and body. Placeholders: {{title}}, {{price}}, {{currency}}, {{link}}`}
              >
                <div className="max-w-sm space-y-2">
                  <ToggleSwitch
                    label="Enabled"
                    checked={form.emailActive}
                    onChange={(v) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, emailActive: v }));
                    }}
                  />
                  <input
                    value={form.emailSubjectRu}
                    onChange={(e) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, emailSubjectRu: e.target.value }));
                    }}
                    placeholder="Subject (Russian)"
                    className={inputClass}
                  />
                  <textarea
                    value={form.emailBodyRu}
                    onChange={(e) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, emailBodyRu: e.target.value }));
                    }}
                    placeholder="Body (Russian)"
                    rows={3}
                    className={`${inputClass} resize-y`}
                  />
                  <p className="text-xs text-gray-400 pt-1">
                    English version (optional) — used for buyers whose saved language is English. Leave blank to always send the version above.
                  </p>
                  <input
                    value={form.emailSubjectEn}
                    onChange={(e) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, emailSubjectEn: e.target.value }));
                    }}
                    placeholder="Subject (English)"
                    className={inputClass}
                  />
                  <textarea
                    value={form.emailBodyEn}
                    onChange={(e) => {
                      setSaved(false);
                      setForm((f) => ({ ...f, emailBodyEn: e.target.value }));
                    }}
                    placeholder="Body (English)"
                    rows={3}
                    className={`${inputClass} resize-y`}
                  />
                </div>
              </Section>

              {error && <p className="text-sm text-red-600 pt-4">{error}</p>}

              <div className="flex items-center gap-3 pt-5 border-t border-gray-100 mt-5">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || deleting}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-brown text-white shadow-sm hover:opacity-90 disabled:opacity-50 transition"
                >
                  {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
                </button>
                {isNew ? (
                  <button
                    type="button"
                    onClick={handleDiscard}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
                  >
                    Discard
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving || deleting}
                    className="px-4 py-2 text-sm font-medium rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition"
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
                {saved && <span className="text-sm text-green-600">Saved</span>}
              </div>
            </div>

            {!isNew && (
              <div className="pt-5 border-t border-gray-100 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 mt-5 lg:mt-0 space-y-6">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Classes</h4>
                    <button type="button" onClick={handleAddClassDraft} className="text-xs font-medium text-brown hover:underline">
                      + Add class
                    </button>
                  </div>
                  <div className="space-y-2">
                    {classDrafts.map((draftId) => (
                      <ClassRow
                        key={`draft-${draftId}`}
                        courseId={course._id}
                        cls={null}
                        token={token}
                        setToaster={setToaster}
                        isNew
                        startExpanded
                        onCreated={(created) => handleClassCreated(draftId, created)}
                        onDiscard={() => handleClassDiscard(draftId)}
                      />
                    ))}
                    {sortedClasses.map((cls) => (
                      <ClassRow
                        key={cls._id}
                        courseId={course._id}
                        cls={cls}
                        token={token}
                        setToaster={setToaster}
                        onSaved={handleClassSaved}
                        onDeleted={handleClassDeleted}
                      />
                    ))}
                    {sortedClasses.length === 0 && classDrafts.length === 0 && (
                      <p className="text-xs text-gray-400">No classes yet.</p>
                    )}
                  </div>
                </div>

                <div className="pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Buyers</h4>
                    {purchases && <Pill tone={purchases.length > 0 ? 'green' : 'gray'}>{purchases.length}</Pill>}
                  </div>
                  {loadingPurchases ? (
                    <p className="text-xs text-gray-400">Loading…</p>
                  ) : (purchases || []).length === 0 ? (
                    <p className="text-xs text-gray-400">No one has purchased this course yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {purchases.map((purchase) => (
                        <div key={purchase._id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                          <span className="min-w-0 truncate">
                            {purchase.userId?.name ||
                              [purchase.userId?.given_name, purchase.userId?.family_name].filter(Boolean).join(' ') ||
                              purchase.userId?.email ||
                              purchase.user_id}
                            {purchase.userId?.email && (
                              <span className="text-xs text-gray-500"> — {purchase.userId.email}</span>
                            )}
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-gray-500">
                              {purchase.finalPrice > 0 && `${formatPassPriceShort(purchase.finalPrice, purchase.currency)} · `}
                              {new Date(purchase.purchasedAt).toLocaleDateString()}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRevokeAccess(purchase)}
                              disabled={revokingPurchaseId === purchase._id}
                              className="text-red-600 text-xs hover:underline disabled:opacity-50"
                            >
                              {revokingPurchaseId === purchase._id ? '…' : 'Remove'}
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setGrantModalOpen(true)}
                    className="text-sm text-brown hover:underline pt-2"
                  >
                    + Grant access to a user
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {grantModalOpen && (
        <GrantCourseAccessModal
          courseId={course._id}
          courseTitle={form.title}
          existingPurchases={purchases || []}
          onClose={() => setGrantModalOpen(false)}
          onSuccess={() => {
            setLoadingPurchases(true);
            getAdminCoursePurchases(token, course._id)
              .then((data) => setPurchases(data.purchases || []))
              .finally(() => setLoadingPurchases(false));
          }}
        />
      )}
    </div>
  );
};

export default function AdminCourses({ token }) {
  const { setToaster } = useAppContext();
  const [courses, setCourses] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [nextDraftId, setNextDraftId] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCourses = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const data = await getAdminCourses(token);
      setCourses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setToaster({ message: 'Failed to load courses', type: 'error', show: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAddDraft = () => {
    setDrafts((prev) => [...prev, nextDraftId]);
    setNextDraftId((id) => id + 1);
  };

  const handleCreated = (draftId, created) => {
    setDrafts((prev) => prev.filter((id) => id !== draftId));
    setCourses((prev) => [...prev, created]);
  };

  const handleDiscard = (draftId) => {
    setDrafts((prev) => prev.filter((id) => id !== draftId));
  };

  const handleSaved = (updated) => {
    setCourses((prev) => prev.map((c) => (c._id === updated._id ? updated : c)));
  };

  const handleDeleted = (id) => {
    setCourses((prev) => prev.filter((c) => c._id !== id));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 space-y-4 text-left">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Courses</h2>
          <p className="text-sm text-gray-600 mt-1">Manage courses, their classes, and the video/text content inside each class.</p>
        </div>
        <button
          type="button"
          onClick={handleAddDraft}
          className="shrink-0 px-4 py-2 text-sm font-medium rounded-md bg-brown text-white shadow-sm hover:opacity-90 transition"
        >
          + Add course
        </button>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {!loading && courses.length === 0 && drafts.length === 0 && (
        <p className="text-sm text-gray-500">No courses yet.</p>
      )}

      <div className="space-y-3">
        {drafts.map((draftId) => (
          <CourseRow
            key={`draft-${draftId}`}
            course={{}}
            token={token}
            setToaster={setToaster}
            isNew
            startExpanded
            onCreated={(created) => handleCreated(draftId, created)}
            onDiscard={() => handleDiscard(draftId)}
          />
        ))}
        {courses.map((course) => (
          <CourseRow
            key={course._id}
            course={course}
            token={token}
            setToaster={setToaster}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
          />
        ))}
      </div>
    </div>
  );
}
